/**
 * ExecutionLogsViewer Component
 *
 * Displays execution logs with filtering and search capabilities.
 * Follows patterns from AppLogsViewer.
 */

import React, { useState, useMemo } from "react";
import { useExecutionLogs } from "../hooks/useExecutionLogs";
import { Select } from "../../../components/ui/Select";
import { Button } from "../../../components/ui/Button";
import { Alert } from "../../../components/ui/Alert";
import { Spinner } from "../../../components/ui/Spinner";
import { formatDate } from "../../../lib/utils/format";
import { getErrorMessage } from "../../../lib/utils/errors";
import type { ExecutionLogsQuery, ExecutionLogLevel, ExecutionLogCategory, ExecutionLogEntry } from "../../../types/elt-job-execution.types";

interface ExecutionLogsViewerProps {
  tenantId: string;
  jobExecutionId: string;
}

/**
 * Log level options
 */
const LEVEL_OPTIONS = [
  { value: "", label: "All Levels" },
  { value: "INFO", label: "Info" },
  { value: "WARN", label: "Warning" },
  { value: "ERROR", label: "Error" },
  { value: "DEBUG", label: "Debug" },
];

/**
 * Category options
 */
const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "agent_metadata", label: "Agent Metadata" },
  { value: "execution_start", label: "Execution Start" },
  { value: "query_execution", label: "Query Execution" },
  { value: "upload", label: "Upload" },
  { value: "execution_complete", label: "Execution Complete" },
];

/**
 * Get color class for log level
 */
const getLevelColor = (level: ExecutionLogLevel): string => {
  switch (level) {
    case "ERROR":
      return "text-red-400";
    case "WARN":
      return "text-yellow-400";
    case "INFO":
      return "text-blue-400";
    case "DEBUG":
      return "text-gray-400";
    default:
      return "text-gray-400";
  }
};

/**
 * Get badge color class for category
 */
const getCategoryBadge = (category: ExecutionLogCategory): string => {
  switch (category) {
    case "agent_metadata":
      return "bg-purple-500/20 text-purple-300";
    case "execution_start":
      return "bg-green-500/20 text-green-300";
    case "query_execution":
      return "bg-blue-500/20 text-blue-300";
    case "upload":
      return "bg-cyan-500/20 text-cyan-300";
    case "execution_complete":
      return "bg-orange-500/20 text-orange-300";
    default:
      return "bg-gray-500/20 text-gray-300";
  }
};

/**
 * Format category for display
 */
const formatCategory = (category: ExecutionLogCategory): string => {
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Render metadata details if present
 */
const MetadataDetails: React.FC<{ log: ExecutionLogEntry }> = ({ log }) => {
  if (log.agentMetadata) {
    const { uptime, memoryTotal, memoryAvailable, diskTotal, diskAvailable } = log.agentMetadata;
    const formatBytes = (bytes: number) => {
      if (bytes === 0) return "N/A";
      const gb = bytes / (1024 * 1024 * 1024);
      return `${gb.toFixed(2)} GB`;
    };
    const formatUptime = (seconds: number) => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    };
    return (
      <div className="mt-1 ml-4 text-xs text-gray-400 space-x-4">
        <span>Uptime: {formatUptime(uptime)}</span>
        <span>
          Memory: {formatBytes(memoryAvailable)} / {formatBytes(memoryTotal)}
        </span>
        {diskTotal > 0 && (
          <span>
            Disk: {formatBytes(diskAvailable)} / {formatBytes(diskTotal)}
          </span>
        )}
      </div>
    );
  }

  if (log.queryMetadata) {
    const { queryName, status, rowCount, executionTimeMs, error } = log.queryMetadata;
    return (
      <div className="mt-1 ml-4 text-xs text-gray-400 space-x-4">
        <span>Query: {queryName}</span>
        <span>Status: {status}</span>
        {rowCount !== undefined && <span>Rows: {rowCount.toLocaleString()}</span>}
        {executionTimeMs !== undefined && <span>Time: {executionTimeMs}ms</span>}
        {error && <span className="text-red-400">Error: {error}</span>}
      </div>
    );
  }

  if (log.uploadMetadata) {
    const { s3Key, status, fileSizeBytes, error } = log.uploadMetadata;
    const formatSize = (bytes?: number) => {
      if (!bytes) return "N/A";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };
    return (
      <div className="mt-1 ml-4 text-xs text-gray-400 space-x-4">
        <span>Key: {s3Key}</span>
        <span>Status: {status}</span>
        {fileSizeBytes && <span>Size: {formatSize(fileSizeBytes)}</span>}
        {error && <span className="text-red-400">Error: {error}</span>}
      </div>
    );
  }

  if (log.executionMetadata) {
    const { status, totalQueries, successfulQueries, failedQueries, totalRowCount, durationMs, error } = log.executionMetadata;
    return (
      <div className="mt-1 ml-4 text-xs text-gray-400 space-x-4">
        <span>Status: {status}</span>
        {totalQueries !== undefined && (
          <span>
            Queries: {successfulQueries || 0}/{totalQueries} (failed: {failedQueries || 0})
          </span>
        )}
        {totalRowCount !== undefined && <span>Total Rows: {totalRowCount.toLocaleString()}</span>}
        {durationMs !== undefined && <span>Duration: {(durationMs / 1000).toFixed(1)}s</span>}
        {error && <span className="text-red-400">Error: {error}</span>}
      </div>
    );
  }

  return null;
};

/**
 * ExecutionLogsViewer component
 */
export const ExecutionLogsViewer: React.FC<ExecutionLogsViewerProps> = ({ tenantId, jobExecutionId }) => {
  const [level, setLevel] = useState<string>("");
  const [category, setCategory] = useState<string>("");

  // Build query parameters
  const query = useMemo<ExecutionLogsQuery>(
    () => ({
      level: (level as ExecutionLogLevel) || undefined,
      category: (category as ExecutionLogCategory) || undefined,
      limit: 500,
    }),
    [level, category],
  );

  // Fetch logs
  const { data, isLoading, error, refetch } = useExecutionLogs(tenantId, jobExecutionId, query);

  const handleRefresh = () => {
    refetch();
  };

  const handleDownloadLogs = () => {
    if (!data?.logs || data.logs.length === 0) {
      return;
    }

    const logsText = data.logs.map((log) => `${log.timestamp} | [${log.level}] [${log.category}] ${log.message}`).join("\n");

    const blob = new Blob([logsText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `execution-${jobExecutionId}-logs-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Level Filter */}
          <div>
            <Select label="Log Level" value={level} onChange={(e) => setLevel(e.target.value)} options={LEVEL_OPTIONS} />
          </div>

          {/* Category Filter */}
          <div>
            <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)} options={CATEGORY_OPTIONS} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={handleRefresh}>
            Refresh
          </Button>
          <Button size="sm" variant="secondary" onClick={handleDownloadLogs} disabled={!data?.logs || data.logs.length === 0}>
            Download Logs
          </Button>
          {data?.logs && <span className="text-sm text-gray-600 ml-auto">{data.logs.length} log entries</span>}
        </div>
      </div>

      {/* Error State */}
      {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Logs Display */}
      {!isLoading && data && (
        <>
          {data.logs.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No logs found for this execution.</p>
              <p className="text-gray-400 text-sm mt-1">Logs are submitted when the execution completes.</p>
            </div>
          ) : (
            <div className="bg-gray-900 rounded-lg p-4 max-h-[500px] overflow-y-auto">
              <div className="font-mono text-sm text-gray-100 space-y-2">
                {data.logs.map((log, index) => (
                  <div key={index} className="hover:bg-gray-800 px-2 py-1.5 rounded transition-colors">
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400 text-xs whitespace-nowrap">[{formatDate(log.timestamp, "yyyy-MM-dd'T'HH:mm:ss.SSS")}]</span>
                      <span className={`font-semibold text-xs ${getLevelColor(log.level)}`}>[{log.level}]</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${getCategoryBadge(log.category)}`}>{formatCategory(log.category)}</span>
                      <span className="text-gray-100 flex-1">{log.message}</span>
                    </div>
                    <MetadataDetails log={log} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Statistics */}
          {data.statistics && (
            <div className="text-xs text-gray-500 text-right">
              Matched: {data.statistics.recordsMatched} | Scanned: {data.statistics.recordsScanned} | Bytes:{" "}
              {(data.statistics.bytesScanned / 1024).toFixed(1)} KB
            </div>
          )}
        </>
      )}
    </div>
  );
};
